import { Box, Circle, Flex, Text } from "@chakra-ui/react";
import * as React from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import {
  FormSection,
  HiddenSection,
  RadioGroup,
  TextField,
} from "@/components/Form";
import { FormLabels } from "@/types";
import { generateDynamicFormFields } from "@/utils/generateDynamicFormFields";

import { SmokeDetector } from "../../types";

type SmokeDetectorsProps = {
  smokeDetectorCount: number;
  labels: FormLabels<SmokeDetector>;
  isEdit: boolean;
};

type SmokeDetectorData = {
  smokeDetectors: SmokeDetector[];
};

export const SmokeDetectors = React.memo(
  ({ smokeDetectorCount, labels, isEdit = false }: SmokeDetectorsProps) => {
    const { register, control, getFieldState, watch } =
      useFormContext<SmokeDetectorData>();
    const { fields, append, remove } = useFieldArray({
      name: "smokeDetectors",
      control,
    });

    const smokeDetectors = watch("smokeDetectors");

    React.useLayoutEffect(() => {
      if (smokeDetectorCount > 0 && !isEdit) {
        remove();
        append(
          generateDynamicFormFields<SmokeDetector>(smokeDetectorCount, {
            smokeDetectorChecked: "",
            smokeDetectorIssues: "",
            smokeDetectorIssueDescription: "",
          }),
        );
      }
    }, [append, smokeDetectorCount, remove, isEdit]);

    React.useEffect(() => {}, [fields]);

    if (smokeDetectorCount < 1) {
      return null;
    }

    return (
      <FormSection name={`Smoke Detectors (${smokeDetectorCount})`}>
        {fields.map((field, index) => (
          <Box marginY="1rem" key={field.id}>
            <Flex alignItems="flex-start">
              <Circle
                marginRight="1rem"
                size="40px"
                bg="primary.500"
                color="white"
                fontWeight="bold"
              >
                {index + 1}
              </Circle>
              <Box marginTop=".5rem">
                <Text as="b" textDecoration="underline">
                  Smoke Detector {index + 1}
                </Text>
                <RadioGroup
                  label={labels.smokeDetectorChecked}
                  options={["Yes", "No"]}
                  name={`smokeDetectors.${index}.smokeDetectorChecked`}
                  control={control}
                  error={
                    getFieldState(
                      `smokeDetectors.${index}.smokeDetectorChecked`,
                    ).error
                  }
                />
                <RadioGroup
                  label={labels.smokeDetectorIssues}
                  options={["Yes", "No"]}
                  name={`smokeDetectors.${index}.smokeDetectorIssues`}
                  control={control}
                  error={
                    getFieldState(`smokeDetectors.${index}.smokeDetectorIssues`)
                      .error
                  }
                />
                <HiddenSection
                  show={smokeDetectors[index].smokeDetectorIssues === "No"}
                >
                  <TextField
                    label={labels.smokeDetectorIssueDescription}
                    registration={register(
                      `smokeDetectors.${index}.smokeDetectorIssueDescription`,
                    )}
                    error={
                      getFieldState(
                        `smokeDetectors.${index}.smokeDetectorIssueDescription`,
                      ).error
                    }
                  />
                </HiddenSection>
              </Box>
            </Flex>
          </Box>
        ))}
      </FormSection>
    );
  },
);
